export type WidgetSize = 'stat' | 'statWide' | 'half' | 'guide' | 'wide' | 'hero';

export type DashboardPeriodDays = number;

export const CUSTOM_PERIOD = 'custom';
export type CustomPeriod = typeof CUSTOM_PERIOD;

export interface DashboardPeriodOption {
  label: string;
  value: DashboardPeriodDays | CustomPeriod;
}

export const DASHBOARD_PERIOD_OPTIONS: DashboardPeriodOption[] = [
  { label: '1 Month', value: 30 },
  { label: '3 Months', value: 90 },
  { label: '6 Months', value: 180 },
];

export const CUSTOM_PERIOD_OPTION: DashboardPeriodOption = { label: 'Custom', value: CUSTOM_PERIOD };

export const DEFAULT_DASHBOARD_PERIOD_DAYS: DashboardPeriodDays = 30;

// ── Add a widget (step 1 of 3): define its data shape here, matching the JSON the ──
// backend streams for this widget. The widget component binds its @Input to this.
export interface ActiveMembersWidget {
  count: number;
  change: number;
  percentOfCardCustomers: number;
}

export interface VisitsPerMemberWidget {
  monthlyVisits: number;
  monthlyVisitsBeforeJoining: number;
}

export interface LiftBreakdownWidget {
  /** Lift drivers, rendered in order. Each bar is sized to its share of the total. */
  components: LiftComponent[];
  /** Optional footnote shown under the bars (e.g. methodology caveat). */
  note?: string | null;
}

export interface LiftComponent {
  label: string;
  amount: number;
}

export interface RewardsThisPeriodWidget {
  pointsEarned: number;
  rewardsRedeemed: number;
  costOfRewards: number;
  mostRedeemed: string | null;
  returnPerDollar: number;
}

export interface AverageTicketWidget {
  member: number | null;
  nonMember: number | null;
}

export type HabitMetricUnit = 'percentChange' | 'visitsDelta' | 'percentReturned';

export interface CustomersByHabitWidget {
  segments: HabitSegment[];
}

export interface HabitSegment {
  label: string;
  description: string;
  memberCustomers: number;
  nonMemberCustomers: number;
  unit: HabitMetricUnit;
  member: number | null;
  nonMember: number | null;
}

export interface CustomersWhoCameBackWidget {
  /** Share of newly-acquired customers who made a repeat visit within `windowDays`, as a percent. */
  returnRate: number;
  /** The return window the rate is measured over, in days. */
  windowDays: number;
  /** Median return rate across same-vertical peers, as a percent. Null when there is no comparable peer group. */
  peerMedianReturnRate?: number | null;
  /** The vertical the peer median is drawn from (e.g. "taqueria"). Null when no peer median is shown. */
  peerGroupLabel?: string | null;
}

export interface WeeklySalesPoint {
  weekStart: string;
  merchantRevenue: number;
  peerAverageRevenue: number | null;
}

export interface SalesComparisonPeriod {
  kind: 'before' | 'since';
  weeks: WeeklySalesPoint[];
}

export interface SalesComparisonPeerGroup {
  name: string;
  merchantCount: number;
  /** Whether the peer group runs a loyalty program. False for the usual (free-tier peers) comparison. */
  peerHasLoyalty: boolean;
}

export interface SalesComparisonWidget {
  joinDate: string;
  trialEndDate?: string;
  /** 1 or 2 entries; a 'since' entry is always present. */
  periods: SalesComparisonPeriod[];
  peerGroup: SalesComparisonPeerGroup;
}

export type MetricFormat = 'currency' | 'percent' | 'signedPercent' | 'decimal';

export interface TierDistribution {
  p25: number;
  median: number;
  p75: number;
  merchantCount: number;
}

export interface CompareMetric {
  key: string;
  /** Static display text, e.g. 'Average ticket'. The underlying comparison window is dynamic and
   * deliberately not named in any label — a duration here would go stale as the window moves. */
  label: string;
  format: MetricFormat;
  /** Decimal places to render with. Defaults by format: currency 2, percent/signedPercent 0, decimal 1. */
  decimals?: number;
  /** null = not enough of this merchant's own data yet. */
  you: number | null;
  withLoyalty: TierDistribution | null;
  withoutLoyalty: TierDistribution | null;
}

export interface PeerVerticalNameWidget {
  level: 'subVertical' | 'primaryVertical';
  /** Display name, plural, e.g. "Taquerias". */
  name: string;
}

export interface CompareWidgetData {
  peerGroup: {
    level: 'subVertical' | 'primaryVertical';
    /** Display name, plural, e.g. 'Taquerias'. Not currently shown — the card uses generic wording. */
    name: string;
  };
  metrics: CompareMetric[];
}

export interface ProgramImpactWidget {
  periodLabel: string;
  addedRevenue: number;
  vsExpectedPercent: number | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  rewardsRedeemed: number | null;
  peerDescriptor: string | null;
  sinceJoining: number | null;
  returnOnPlanCost: number | null;
  comparedAgainst: string | null;
}

export interface NextStepsWidget {
  steps: NextStep[];
}

export interface NextStep {
  title: string;
  body: string;
  ctaLabel: string;
  emphasis?: 'primary' | 'secondary';
}
